// src/components/AracMarkaModel.js

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, doc, setDoc, deleteDoc, updateDoc, arrayUnion, arrayRemove, onSnapshot, query, orderBy, writeBatch } from 'firebase/firestore';
import { vehicleData } from '../data/vehicleData';
import {
    Box, Typography, Button, TextField, Grid, List, ListItem,
    ListItemText, ListItemSecondaryAction, IconButton, Paper,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip, Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';

const AracMarkaModel = () => {
    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [loading, setLoading] = useState(true);

    // Dialog states
    const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
    const [isModelModalOpen, setIsModelModalOpen] = useState(false);
    const [newBrandName, setNewBrandName] = useState('');
    const [newModelName, setNewModelName] = useState('');

    // Markaları çek
    useEffect(() => {
        const q = query(collection(db, "vehicleBrands"), orderBy("name", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const brandList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setBrands(brandList);
            setLoading(false);

            // Eğer seçili marka varsa güncelle (model ekleyince vs.)
            if (selectedBrand) {
                const updatedSelected = brandList.find(b => b.id === selectedBrand.id);
                if (updatedSelected) setSelectedBrand(updatedSelected);
            }
        });
        return () => unsubscribe();
    }, [selectedBrand]);

    // Statik verileri Firestore'a aktar
    const handleSeedData = async () => {
        if (!window.confirm("Mevcut 'vehicleData.js' dosyasındaki tüm marka ve modelleri veritabanına aktarmak istiyor musunuz?")) return;

        setLoading(true);
        try {
            const batch = writeBatch(db);
            const brandNames = Object.keys(vehicleData);

            for (const brandName of brandNames) {
                const models = vehicleData[brandName].map(m => m.model);
                const docRef = doc(db, "vehicleBrands", brandName);
                batch.set(docRef, {
                    name: brandName,
                    models: models
                });
            }

            await batch.commit();
            alert("Tüm veri başarıyla aktarıldı!");
        } catch (error) {
            console.error("Aktarım hatası:", error);
            alert("Veri aktarılırken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    // Marka İşlemleri
    const handleAddBrand = async () => {
        if (!newBrandName.trim()) return;
        try {
            await setDoc(doc(db, "vehicleBrands", newBrandName.trim()), {
                name: newBrandName.trim(),
                models: []
            });
            setNewBrandName('');
            setIsBrandModalOpen(false);
        } catch (error) {
            console.error("Marka ekleme hatası:", error);
            alert("Marka eklenirken hata oluştu.");
        }
    };

    const handleDeleteBrand = async (brandId) => {
        if (!window.confirm(`${brandId} markasını ve tüm modellerini silmek istediğinize emin misiniz?`)) return;
        try {
            await deleteDoc(doc(db, "vehicleBrands", brandId));
            if (selectedBrand?.id === brandId) setSelectedBrand(null);
        } catch (error) {
            console.error("Marka silme hatası:", error);
        }
    };

    // Model İşlemleri
    const handleAddModel = async () => {
        if (!selectedBrand || !newModelName.trim()) return;
        try {
            const brandRef = doc(db, "vehicleBrands", selectedBrand.id);
            await updateDoc(brandRef, {
                models: arrayUnion(newModelName.trim())
            });
            setNewModelName('');
            setIsModelModalOpen(false);
        } catch (error) {
            console.error("Model ekleme hatası:", error);
            alert("Model eklenirken hata oluştu.");
        }
    };

    const handleDeleteModel = async (modelName) => {
        if (!window.confirm(`${modelName} modelini silmek istediğinize emin misiniz?`)) return;
        try {
            const brandRef = doc(db, "vehicleBrands", selectedBrand.id);
            await updateDoc(brandRef, {
                models: arrayRemove(modelName)
            });
        } catch (error) {
            console.error("Model silme hatası:", error);
        }
    };

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Araç Marka ve Modelleri</Typography>
                <Box>
                    {brands.length === 0 && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<UploadIcon />}
                            onClick={handleSeedData}
                            sx={{ mr: 2 }}
                        >
                            Varsayılan Verileri Yükle
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsBrandModalOpen(true)}
                    >
                        Yeni Marka
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ height: '70vh' }}>
                {/* Marka Listesi */}
                <Grid item xs={12} md={4} sx={{ height: '100%' }}>
                    <Paper sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="subtitle1" sx={{ p: 2, bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}>
                            Markalar ({brands.length})
                        </Typography>
                        <List sx={{ flexGrow: 1, overflow: 'auto' }}>
                            {brands.map((brand) => (
                                <ListItem
                                    key={brand.id}
                                    button
                                    selected={selectedBrand?.id === brand.id}
                                    onClick={() => setSelectedBrand(brand)}
                                    secondaryAction={
                                        <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteBrand(brand.id)} size="small">
                                            <DeleteIcon />
                                        </IconButton>
                                    }
                                >
                                    <ListItemText primary={brand.name} />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                {/* Model Listesi */}
                <Grid item xs={12} md={8} sx={{ height: '100%' }}>
                    <Paper sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1">
                                {selectedBrand ? `${selectedBrand.name} Modelleri (${selectedBrand.models?.length || 0})` : 'Model Listesi'}
                            </Typography>
                            {selectedBrand && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setIsModelModalOpen(true)}
                                >
                                    Model Ekle
                                </Button>
                            )}
                        </Box>

                        <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
                            {!selectedBrand ? (
                                <Alert severity="info">Modelleri görmek için soldan bir marka seçin.</Alert>
                            ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {selectedBrand.models && selectedBrand.models.length > 0 ? (
                                        selectedBrand.models.sort().map((model, index) => (
                                            <Chip
                                                key={index}
                                                label={model}
                                                onDelete={() => handleDeleteModel(model)}
                                                sx={{ m: 0.5 }}
                                            />
                                        ))
                                    ) : (
                                        <Typography color="text.secondary">Bu markaya ait henüz model eklenmemiş.</Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Marka Ekleme Modalı */}
            <Dialog open={isBrandModalOpen} onClose={() => setIsBrandModalOpen(false)}>
                <DialogTitle>Yeni Marka Ekle</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Marka Adı"
                        fullWidth
                        value={newBrandName}
                        onChange={(e) => setNewBrandName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsBrandModalOpen(false)}>İptal</Button>
                    <Button onClick={handleAddBrand} variant="contained">Ekle</Button>
                </DialogActions>
            </Dialog>

            {/* Model Ekleme Modalı */}
            <Dialog open={isModelModalOpen} onClose={() => setIsModelModalOpen(false)}>
                <DialogTitle>{selectedBrand?.name} İçin Yeni Model</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Model Adı"
                        fullWidth
                        value={newModelName}
                        onChange={(e) => setNewModelName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsModelModalOpen(false)}>İptal</Button>
                    <Button onClick={handleAddModel} variant="contained">Ekle</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AracMarkaModel;
